import { uniq } from 'lodash';
import { create } from 'zustand';

import type { IRentalProduct } from '../../types';

interface IAppStoreStateRent {
  step: number;
  products: IRentalProduct[];
  filteredProducts: IRentalProduct[];
  filter: {
    system: string;
    manufacturer: string;
    search: string;
    type: string;
    kontur: string;
    material: string;
  };
  availableFilter: {
    system: string[];
    type: string[];
    kontur: string[];
    material: string[];
    manufacturer: string[];
  };
  showRentInfo: boolean;
  showErrors: boolean;
  setShowRentInfo: (value: boolean) => void;
  setShowErrors: (value?: boolean) => void;
  resetAppState: () => void;
  setProducts: (products: IRentalProduct[]) => void;
  setFilteredProducts: (products: IRentalProduct[]) => void;
  setAvailableFilter: (availableFilter: IAppStoreStateRent['availableFilter']) => void;
  setFilter: (propertyKey: string, value: string) => void;
  setFilterAdvanced: (propertyKey: string, value: string) => void;
  increase: (by: number) => void;
  decrease: (by: number) => void;
  setStep: (step: number) => void;
}

const initialState = {
  showRentInfo: false,
  step: 1,
  products: [],
  filteredProducts: [],
  filter: {
    system: '',
    manufacturer: '',
    search: '',
    type: '',
    kontur: '',
    material: ''
  },
  availableFilter: {
    system: [],
    type: [],
    kontur: [],
    material: [],
    manufacturer: []
  },
  showErrors: false,
  selectedProduct: undefined
};

function filterProducts(
  products: IRentalProduct[],
  filter: IAppStoreStateRent['filter'],
  excludeKey?: string
): IRentalProduct[] {
  return products.filter(productItem => {
    if (
      excludeKey !== 'search'
      && !!filter.search
      && (
        !productItem.name.toLowerCase().includes(filter.search.toLowerCase())
        && !productItem.id.includes(filter.search)
      )
    ) {
      return false;
    }

    if (
      excludeKey !== 'system'
      && !!filter.system
      && !productItem.system.includes(filter.system)
    ) {
      return false;
    }

    if (excludeKey !== 'type' && !!filter.type && productItem.type !== filter.type) {
      return false;
    }

    if (
      excludeKey !== 'kontur'
      && !!filter.kontur
      && !productItem.kontur.includes(filter.kontur)
    ) {
      return false;
    }

    if (
      excludeKey !== 'material'
      && !!filter.material
      && !productItem.material.includes(filter.material)
    ) {
      return false;
    }

    if (
      excludeKey !== 'manufacturer'
      && !!filter.manufacturer
      && !productItem.manufacturer.includes(filter.manufacturer)
    ) {
      return false;
    }

    return true;
  });
}

const useAppStoreRent = create<IAppStoreStateRent>(set => ({
  ...initialState,
  setShowErrors: (value = true) => {
    set({ showErrors: value });
  },
  resetAppState: () => {
    set(initialState);
  },
  setProducts: (products: IRentalProduct[]) => set({ products }),
  setFilteredProducts: (filteredProducts: IRentalProduct[]) => set({ filteredProducts }),
  setFilter: (propertyKey: string, value: string) => set(state => ({
    filter: {
      ...state.filter,
      [propertyKey]: value
    }
  })),
  setFilterAdvanced: (propertyKey: string, value: string) => {
    set(state => {
      const filter = propertyKey === 'reset' ? {
        system: '',
        manufacturer: '',
        search: '',
        type: '',
        kontur: '',
        material: ''
      } : {
        ...state.filter,
        [propertyKey]: value
      };

      const filteredProducts = filterProducts(state.products, filter);

      const tmpAvailableFilter: IAppStoreStateRent['availableFilter'] = {
        system: [],
        type: [],
        kontur: [],
        material: [],
        manufacturer: []
      };

      const systemProducts = filterProducts(state.products, filter, 'system');

      tmpAvailableFilter.system = uniq(systemProducts.flatMap(p => p.system)).sort();

      const typeProducts = filterProducts(state.products, filter, 'type');

      tmpAvailableFilter.type = uniq(typeProducts.map(p => p.type)).sort();

      const konturProducts = filterProducts(state.products, filter, 'kontur');

      tmpAvailableFilter.kontur = uniq(konturProducts.flatMap(p => p.kontur)).sort();

      const materialProducts = filterProducts(state.products, filter, 'material');

      tmpAvailableFilter.material = uniq(materialProducts.flatMap(p => p.material)).sort();

      const manufacturerProducts = filterProducts(state.products, filter, 'manufacturer');

      tmpAvailableFilter.manufacturer = uniq(manufacturerProducts.flatMap(p => p.manufacturer)).sort();

      return {
        filter,
        availableFilter: tmpAvailableFilter,
        filteredProducts
      };
    });
  },
  setShowRentInfo: (showRentInfo: boolean) => set({ showRentInfo }),
  setAvailableFilter: (availableFilter: IAppStoreStateRent['availableFilter']) => set({ availableFilter }),
  increase: (by: number) => set(state => ({ step: state.step + by })),
  decrease: (by: number) => set(state => ({ step: state.step - by })),
  setStep: (step: number) => set(() => {
    let stepToSet;

    // if (
    //   // No product selected -> jump to product select
    //   step === 3
    //   && !state.selectedProduct
    // ) {
    //   stepToSet = 2;
    // } else {
    //   stepToSet = step;
    // }

    // eslint-disable-next-line prefer-const
    stepToSet = step;

    return {
      step: stepToSet
    };
  })
}));

export default useAppStoreRent;
